import { useMemo, useState } from 'react';
import { handleErrorToast } from 'src/components/molecules';
import {
  useGetCaseAttributesQuery,
  useGetSubmissionDateQuery,
  useUpdateCaseAttributesMutation,
  useUpdateSubmissionDateMutation,
} from 'src/graphql';
import { calculateCaseDates } from 'src/utils';

type CaseDate = 'submittedAt' | 'readAt' | 'investigatingAt' | 'closedAt';

export const useDraftCaseAttributes = (reportID: string, caseId?: string) => {
  const { data: submissionDate, refetch: refetchSubmissionDate } =
    useGetSubmissionDateQuery({
      report: reportID,
    });

  const { data: caseAttributes, refetch: refetchCaseAttributes } =
    useGetCaseAttributesQuery(
      {
        caseId: caseId ?? '',
      },
      {
        enabled: !!caseId,
      },
    );

  const { mutate: updateSubmissionDate } = useUpdateSubmissionDateMutation({
    onSuccess: () => {
      refetchSubmissionDate();
    },
  });
  const { mutate: updateCaseAttributes } = useUpdateCaseAttributesMutation({
    onSuccess: () => {
      refetchCaseAttributes();
    },
  });

  const caseDates = calculateCaseDates({
    submittedAt: submissionDate?.getSubmissionDate,
    readAt: caseAttributes?.getCaseAttributes?.readAt,
    investigatingAt: caseAttributes?.getCaseAttributes?.investigatingAt,
    closedAt: caseAttributes?.getCaseAttributes?.closedAt,
  });

  const [errors, setErrors] = useState({
    submittedAt: false,
    readAt: false,
    investigatingAt: false,
    closedAt: false,
  });

  const [tempCaseDates, setTempCaseDates] = useState({
    submittedAt: caseDates.submittedAt.value ?? '',
    readAt: caseDates.readAt.value ?? '',
    investigatingAt: caseDates.investigatingAt.value ?? '',
    closedAt: caseDates.closedAt.value ?? '',
  });

  useMemo(() => {
    const newCaseDates = calculateCaseDates({
      submittedAt: submissionDate?.getSubmissionDate,
      readAt: caseAttributes?.getCaseAttributes?.readAt,
      investigatingAt: caseAttributes?.getCaseAttributes?.investigatingAt,
      closedAt: caseAttributes?.getCaseAttributes?.closedAt,
    });
    setTempCaseDates({
      submittedAt: newCaseDates.submittedAt.value ?? '',
      readAt: newCaseDates.readAt.value ?? '',
      investigatingAt: newCaseDates.investigatingAt.value ?? '',
      closedAt: newCaseDates.closedAt.value ?? '',
    });
  }, [submissionDate, caseAttributes]);

  const updateCaseDate = (date: string, key: CaseDate) => {
    if (key === 'submittedAt' && date !== '') {
      updateSubmissionDate(
        {
          submittedAt: new Date(date),
          report: reportID,
        },
        {
          onError: () => {
            handleErrorToast({
              toastMessage: 'Unable to update submission date',
            });
          },
        },
      );
    } else if (caseId) {
      const clearDate = date === '' || date === null;
      updateCaseAttributes(
        {
          caseAttributes: {
            caseId,
            [key]: clearDate ? null : new Date(date),
          },
          postSubmission: false,
        },
        {
          onError: () => {
            handleErrorToast({
              toastMessage: `Unable to update ${key.substring(
                0,
                key.length - 2,
              )} date`,
            });
          },
        },
      );
    }
  };

  const handleOnChange = (
    date: string,
    isValidDate: boolean,
    key: CaseDate,
  ) => {
    // Don't allow clearing of submittedAt
    if (key === 'submittedAt' && date === '') {
      return;
    }

    // Update dates to display in datepicker
    setTempCaseDates((prev) => ({ ...prev, [key]: date }));

    // Update in backend if valid
    if (isValidDate) {
      updateCaseDate(date, key);
    }
  };

  const handleOnBlur = (isValidDate: boolean, key: CaseDate) => {
    const date = tempCaseDates[key];
    const existingDate = caseDates[key].value;

    // Display errors if invalid
    setErrors((prev) => ({ ...prev, [key]: !isValidDate }));

    // Don't update if submittedAt is cleared
    if (key === 'submittedAt' && date === '') {
      return;
    }

    // If date is the same as the one in the backend, do nothing
    if (date === existingDate) {
      return;
    }

    if (isValidDate) {
      updateCaseDate(date, key);
    }
  };

  return {
    caseDates,
    tempCaseDates,
    errors,
    handleOnChange,
    handleOnBlur,
  };
};
